/**
 * YouTube Audio Extractor Service
 * Handles YouTube URL detection and audio extraction for music analysis
 */

export interface YouTubeUrlInfo {
  videoId: string;
  title?: string;
  duration?: string;
  isValid: boolean;
}

export interface AudioExtractionResult {
  audioBlob: Blob;
  filename: string;
  duration?: number;
  title?: string;
}

class YouTubeAudioExtractor {
  private readonly YOUTUBE_URL_PATTERNS = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/
  ];

  /**
   * Detect if a string contains a YouTube URL
   */
  detectYouTubeUrl(text: string): YouTubeUrlInfo | null {
    for (const pattern of this.YOUTUBE_URL_PATTERNS) {
      const match = text.match(pattern);
      if (match) {
        return {
          videoId: match[1],
          isValid: true
        };
      }
    }
    return null;
  }

  /**
   * Extract all YouTube URLs from a text string
   */
  extractYouTubeUrls(text: string): YouTubeUrlInfo[] {
    const urls: YouTubeUrlInfo[] = [];
    
    for (const pattern of this.YOUTUBE_URL_PATTERNS) {
      const globalPattern = new RegExp(pattern, 'g');
      let match;
      while ((match = globalPattern.exec(text)) !== null) {
        urls.push({
          videoId: match[1],
          isValid: true
        });
      }
    }
    
    return urls;
  }

  /**
   * Get YouTube video info (title, duration, etc.)
   * Note: This is a placeholder implementation. In a real application, you would:
   * 1. Use YouTube API to get video metadata
   * 2. Use a server-side service to extract audio
   * 3. Handle rate limiting and API quotas
   */
  async getVideoInfo(videoId: string): Promise<YouTubeUrlInfo> {
    try {
      // Placeholder implementation - in reality you'd call YouTube API
      // For now, we'll return basic info
      return {
        videoId,
        title: `Video ${videoId}`,
        duration: 'Unknown',
        isValid: true
      };
    } catch (error) {
      console.error('Error fetching video info:', error);
      return {
        videoId,
        isValid: false
      };
    }
  }

  /**
   * Extract audio from YouTube video
   * Note: This is a placeholder implementation. In a real application, you would:
   * 1. Use a server-side service (like youtube-dl, yt-dlp, or similar)
   * 2. Handle copyright restrictions
   * 3. Respect YouTube's terms of service
   * 4. Use appropriate audio quality settings
   */
  async extractAudio(videoId: string): Promise<AudioExtractionResult> {
    try {
      // Placeholder implementation
      // In a real application, you would:
      // 1. Call your backend API to extract audio
      // 2. Handle the audio download and conversion
      // 3. Return the audio blob
      
      throw new Error('Audio extraction not implemented yet. This requires a backend service.');
      
      // Example of what the implementation would look like:
      /*
      const response = await fetch('/api/extract-youtube-audio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to extract audio');
      }
      
      const audioBlob = await response.blob();
      const videoInfo = await this.getVideoInfo(videoId);
      
      return {
        audioBlob,
        filename: `${videoInfo.title || videoId}.mp3`,
        title: videoInfo.title
      };
      */
    } catch (error) {
      console.error('Error extracting audio:', error);
      throw error;
    }
  }

  /**
   * Validate YouTube URL and extract video ID
   */
  validateAndExtractVideoId(url: string): string | null {
    const urlInfo = this.detectYouTubeUrl(url);
    return urlInfo ? urlInfo.videoId : null;
  }

  /**
   * Get standard YouTube thumbnail URL
   */
  getThumbnailUrl(videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'medium'): string {
    return `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`;
  }

  /**
   * Get YouTube video URL from video ID
   */
  getVideoUrl(videoId: string): string {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }

  /**
   * Check if URL is a valid YouTube URL
   */
  isYouTubeUrl(url: string): boolean {
    return this.YOUTUBE_URL_PATTERNS.some(pattern => pattern.test(url));
  }

  /**
   * Create a File object from audio blob for analysis
   */
  createAudioFileFromBlob(audioBlob: Blob, filename: string): File {
    return new File([audioBlob], filename, {
      type: 'audio/mpeg',
      lastModified: Date.now()
    });
  }

  /**
   * Show user-friendly error messages
   */
  getErrorMessage(error: any): string {
    if (error.message?.includes('not implemented')) {
      return 'YouTube audio extraction requires a backend service. Please upload an audio file directly for now.';
    }
    
    if (error.message?.includes('copyright')) {
      return 'This video may be copyright protected and cannot be processed.';
    }
    
    if (error.message?.includes('not found')) {
      return 'YouTube video not found. Please check the URL and try again.';
    }
    
    return 'Failed to extract audio from YouTube video. Please try again or upload an audio file directly.';
  }
}

export const youtubeAudioExtractor = new YouTubeAudioExtractor();

/**
 * Backend Implementation Notes:
 * 
 * To implement YouTube audio extraction, you would need a backend service that:
 * 
 * 1. Uses libraries like youtube-dl, yt-dlp, or similar
 * 2. Handles rate limiting and API quotas
 * 3. Respects copyright restrictions
 * 4. Converts video to audio format suitable for analysis
 * 5. Manages temporary file storage
 * 
 * Example backend endpoint (Node.js/Express):
 * 
 * ```javascript
 * app.post('/api/extract-youtube-audio', async (req, res) => {
 *   const { videoId } = req.body;
 *   
 *   try {
 *     // Use youtube-dl or similar to extract audio
 *     const audioPath = await extractAudioFromYoutube(videoId);
 *     
 *     // Stream the audio file back
 *     res.sendFile(audioPath);
 *     
 *     // Clean up temporary file
 *     fs.unlinkSync(audioPath);
 *   } catch (error) {
 *     res.status(500).json({ error: error.message });
 *   }
 * });
 * ```
 * 
 * Remember to:
 * - Check YouTube's Terms of Service
 * - Handle copyright restrictions appropriately
 * - Implement proper error handling
 * - Add rate limiting to prevent abuse
 * - Consider using YouTube's official API for metadata
 */